import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Spinner, Badge, Container, Image, Row, Col, Card } from 'react-bootstrap';
import { fetchCustomerTransactions, downloadInvoice } from '../services/api';

// PDF Icon
const FilePdfIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="text-danger" viewBox="0 0 16 16">
    <path d="M5.523 12.424c.14-.082.293-.162.459-.238a7.878 7.878 0 0 1-.45.606c-.28.337-.498.516-.635.572a.266.266 0 0 1-.035.012.282.282 0 0 1-.026-.044c-.056-.11-.054-.216.04-.36.106-.165.319-.354.647-.548zm2.455-1.647c-.119.025-.237.05-.356.078a21.148 21.148 0 0 0 .5-1.05 12.045 12.045 0 0 0 .51.858c-.217.032-.436.07-.654.114zm2.525.939a3.881 3.881 0 0 1-.435-.41c.228.005.434.022.612.054.317.057.466.147.518.209a.095.095 0 0 1 .026.064.436.436 0 0 1-.06.2.307.307 0 0 1-.094.124.107.107 0 0 1-.069.015c-.09-.003-.258-.066-.498-.256zM8.278 6.97c-.04.244-.108.521-.2.822a20.223 20.223 0 0 1-.018-1.65c.045.251.1.534.166.812.032.14.06.29.08.438z"/>
    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
  </svg>
);

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/><path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/></svg>
);

function TransactionModal({ show, onHide, customerId, customerName , canDownloadInvoice }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  // --- Load Transactions ---
  useEffect(() => {
    if (show && customerId) {
      loadTransactions();
    }
  }, [show, customerId]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await fetchCustomerTransactions(customerId, { limit: 50 });
      if (response.data && response.data.success) {
        setTransactions(response.data.data.items || []);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error("Error loading transactions:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // --- Download Invoice Logic ---
  const handleDownloadInvoice = async (receiptId, receiptNumber) => {
    if (!receiptId) return;
    setDownloadingId(receiptId);
    try {
      const response = await downloadInvoice(receiptId);
      
      // Create Blob from binary response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Trigger download via temporary anchor
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${receiptNumber || 'Invoice'}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      alert("Failed to download invoice. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const formatCurrency = (amount, currency) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch (e) {
      return `${currency || '$'} ${amount}`;
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'paid' || s === 'succeeded') return <Badge bg="success">Paid</Badge>;
    if (s === 'pending') return <Badge bg="warning" text="dark">Pending</Badge>;
    if (s === 'failed') return <Badge bg="danger">Failed</Badge>;
    return <Badge bg="secondary">{status}</Badge>;
  };

  // Helper to format date as mm/dd/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '-';
    
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    
    return `${month}/${day}/${year}`;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen={true}
      aria-labelledby="transaction-modal-title"
      contentClassName="d-flex flex-column vh-100 bg-light"
    >
      <div
        className="shadow-sm"
        style={{ padding: "0.5rem 1rem", background: "#0B5798" }}
      >
        <Container
          fluid
          className="px-4 d-flex align-items-center justify-content-between"
        >
          <div className="d-flex align-items-center">
            <span className="fw-bold text-white fs-5">Transaction History</span>
          </div>
          <Button
            variant="close"
            aria-label="Close"
            className="btn-close-white"
            onClick={onHide}
          ></Button>
        </Container>
      </div>

      <Modal.Body className="p-4 bg-light flex-grow-1 overflow-auto">
        <Container fluid>
          {/* <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0 text-primary">Transactions for <span className="fw-bold text-dark">{customerName}</span></h5>
              <Button variant="outline-secondary" size="sm" onClick={onHide}>Close</Button>
          </div> */}

          <Card className="shadow-sm border-0">
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-2">Loading transactions...</p>
                </div>
              ) : (
                <Table responsive hover className="mb-0 align-middle">
                  <thead className="bg-white border-bottom">
                    <tr>
                      <th className="py-3 ps-4">ID</th>
                      <th className="py-3 ps-2">TRANSACTION REFERENCE</th>
                      <th className="py-3 text-center">STATUS</th>
                      <th className="py-3 text-center">PAID ON</th>
                      <th className="py-3 text-center">CC NUMBER</th>
                      <th className="py-3 text-end">AMOUNT</th>
                      <th className="py-3 text-center pe-4">RECEIPT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length > 0 ? (
                      transactions.map((txn) => (
                        <tr key={txn.id}>
                          <td className="ps-4 fw-bold text-secondary">
                            {txn.receiptNumber || txn.id}
                          </td>
                          <td className="ps-2 fw-bold text-secondary">
                            {txn.stripePaymentIntentId || "-"}
                          </td>
                          <td className="text-center">
                            {getStatusBadge(txn.status)}
                          </td>
                          <td className="text-center">
                            {formatDate(txn.paidAt)}
                          </td>
                          <td className="text-center text-muted">
                            {txn.paymentMethodSummary || "-"}
                          </td>
                          <td className="text-end fw-bold">
                            {formatCurrency(txn.totalAmount, txn.currency)}
                          </td>
                          <td className="text-center pe-4">
                            {canDownloadInvoice && (
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() =>
                                  handleDownloadInvoice(
                                    txn.id,
                                    txn.receiptNumber,
                                  )
                                }
                                disabled={downloadingId === txn.id}
                                title="Download Invoice"
                              >
                                {downloadingId === txn.id ? (
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                  />
                                ) : (
                                  <DownloadIcon />
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          <div className="mb-2" style={{ fontSize: "2rem" }}>
                            🧾
                          </div>
                          No transactions found for this customer.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </Modal.Body>

      <footer className="bg-white py-3 mt-auto shadow-sm border-top">
        <Container fluid className="px-4">
          <Row>
            <Col className="text-muted" style={{ fontSize: "0.9rem" }}>
              &copy; {new Date().getFullYear()} Smart Monitor Corp - All Rights
              Reserved.
            </Col>
          </Row>
        </Container>
      </footer>
    </Modal>
  );
}

export default TransactionModal;